import { marcosDaSemana } from './marcos';
import { metaDaSemana } from './meta-e-bloqueio';
import { CartaoDaMetaDaEquipe } from './MetaDaEquipe';
import { useEffect, useState } from 'react';
import { lerDestaques, podeFazer } from '../../acesso';
import type { Destaques, Eu } from '../../acesso';
import { ErroDeArea } from '../../design/Estados';
import { AvisosDaHome } from '../administracao/AvisosDaHome';
import { Icone } from '../../design/Icone';
import { reais } from '../../produtos';
import { mostraValor, useValoresOcultos } from '../../design/sigilo';
import { TopoMovel } from '../../shell/ShellBalconista';
import { lerMeuGiro } from '../../giro';
import type { MeuBloco } from '../../giro';
import {
  ROTULO_DO_PROGRAMA,
  SIGLA_DO_PROGRAMA,
  hora,
  resumoDoBalconista,
  quandoVence,
  vencendoNaSemana,
} from '../../vendas';
import type { MinhaVenda, ResumoDoBalconista, VencendoNaSemana } from '../../vendas';
import './balconista.css';

/**
 * TELA 06 — HOME DO BALCONISTA. "O que eu faço agora": uma ação principal, um resumo da semana.
 *
 * Com Giro pendente hoje, o cartão do Giro vem primeiro (tela 19C); sem Giro, a ação principal é
 * registrar venda — a Home nunca desenha missão de mentira. Tudo o que tem número vem de `/api/balconista/resumo`.
 * A premiação da semana é a PREVISTA (soma do que foi lançado): quem paga é a conferência.
 */
export function Inicio({ eu }: { eu: Eu | null }) {
  const ocultos = useValoresOcultos();
  const [resumo, setResumo] = useState<ResumoDoBalconista | null>(null);
  const [erro, setErro] = useState(false);
  const [giro, setGiro] = useState<MeuBloco | null>(null);
  // Horário do Giro (dono, 28/09): antes da hora o cartão só avisa quando começa.
  const [giroAs, setGiroAs] = useState<string | null>(null);
  useEffect(() => {
    if (!eu?.capacidades.includes('giro.executar')) return;
    void lerMeuGiro().then((x) => {
      if (!x.ok) return;
      setGiro(
        x.dados.blocos.find((b) => ['nao_iniciado', 'contagem', 'reposicao'].includes(b.fase)) ??
          null,
      );
      setGiroAs(x.dados.antesDoHorario ? x.dados.horario : null);
    });
  }, [eu]);

  const carregar = () => {
    setErro(false);
    void resumoDoBalconista().then((r) => {
      if (r.ok) setResumo(r.dados);
      else setErro(true);
    });
  };
  useEffect(carregar, []);
  useEffect(() => {
    if (resumo && eu) marcosDaSemana(eu.login, resumo);
  }, [resumo, eu]);

  if (erro)
    return (
      <>
        <TopoMovel eu={eu} />
        <div className="bh">
          <ErroDeArea oQue="seu resumo" aoTentar={carregar} />
        </div>
      </>
    );

  const r = resumo;
  // D6: a barra é a do CHEQUE (confirmados com o HOS); o lançado aparece com esse nome
  const m = r ? metaDaSemana(r) : null;
  const pct = m?.pct ?? 0;
  const faltam = m?.faltam ?? 0;
  const vendasHoje = r?.ultimas.filter((v) => v.status === 'ativo') ?? [];

  return (
    <>
      <TopoMovel eu={eu} />
      <div className="bh" aria-busy={r === null}>
        <div className="bh-dia-movel">HOJE · {diaDaSemana().toUpperCase()}</div>
        <div className="bh-titulo-pc">
          <h1>Hoje</h1>
          <span>{diaPorExtenso()}</span>
        </div>

        {/* Tela 30 B: no máximo três avisos, com "ver todos". Nenhum bloqueia o trabalho. */}
        {eu?.capacidades.includes('comunicacao.ler') && <AvisosDaHome />}

        <DestaquesDoDia />

        {r?.bloqueioGiro && (
          <div className="bh-manutencao" role="alert">
            <Icone nome="warn" tamanho={16} traco={1.8} />
            <span>
              <b>Suas vendas estão bloqueadas hoje.</b> {r.bloqueioGiro} Dúvida? Fale com o gestor.
            </span>
          </div>
        )}

        {r?.manutencao && (
          <div className="bh-manutencao" role="status">
            <Icone nome="warn" tamanho={16} traco={1.8} />
            <span>
              <b>InovaPay em manutenção.</b> {r.manutencao}
            </span>
          </div>
        )}

        {/* Meta da equipe com brinde (1.0): só o total da equipe, nunca o de um colega. */}
        <CartaoDaMetaDaEquipe />

        <div className="bh-grade">
          {giro && (
            <a className="bh-cartao bh-giro" href="#/giro">
              <span className="bh-giro-topo">
                <Icone nome="cycle" tamanho={16} traco={1.8} /> Giro de Prateleira
              </span>
              <span className="bh-giro-faixa">
                {giro.setor} · B{giro.numero} · {giro.faixa}
              </span>
              <span className="bh-giro-prog">
                <span>{giro.fase === 'reposicao' ? 'Reposição' : 'Contagem'}</span>
                <b>
                  {giro.fase === 'reposicao' ? giro.repostos : giro.contados} /{' '}
                  {giro.fase === 'reposicao' ? giro.aRepor : giro.total}
                </b>
              </span>
              <span className="bh-giro-barra">
                <i
                  style={{
                    width: `${String(Math.round((giro.fase === 'reposicao' ? giro.repostos / Math.max(1, giro.aRepor) : giro.contados / Math.max(1, giro.total)) * 100))}%`,
                  }}
                />
              </span>
              <span className="bh-giro-botao">
                {giroAs
                  ? `Começa às ${giroAs}`
                  : giro.contados || giro.fase === 'reposicao'
                    ? 'Continuar'
                    : 'Iniciar'}
              </span>
            </a>
          )}
          <a className="bh-cartao bh-registrar" href="#/vendas">
            <span className="bh-registrar-mais" aria-hidden="true">
              <Icone nome="plus" tamanho={20} traco={1.6} />
            </span>
            <span className="bh-registrar-texto">
              <span className="bh-registrar-titulo">Registrar venda</span>
              <span className="bh-registrar-sub">
                <span className="bh-so-movel">
                  {r && r.itensHoje > 0
                    ? `${String(r.itensHoje)} ${r.itensHoje === 1 ? 'item lançado' : 'itens lançados'} hoje`
                    : textoDosProgramas(r)}
                </span>
                <span className="bh-so-pc">
                  {r
                    ? `${String(r.itensHoje)} ${r.itensHoje === 1 ? 'item lançado' : 'itens lançados'} hoje${mostraValor(ocultos, r.premiacaoHoje) ? ` · ${reais(r.premiacaoHoje)}` : ''}`
                    : 'Carregando…'}
                </span>
              </span>
            </span>
            <span className="bh-registrar-cam" aria-hidden="true">
              <Icone nome="cam" tamanho={19} traco={1.5} />
            </span>
            <span className="bh-registrar-abrir">Abrir</span>
          </a>

          <section className="bh-cartao bh-semana" aria-label="Sua semana">
            <div className="bh-cartao-topo">
              <h2>Sua semana</h2>
              <a href="#/desempenho">Meu desempenho</a>
            </div>
            <div className="bh-semana-corpo">
              <div className="bh-linha">
                <span className="bh-rotulo">Meta obrigatória</span>
                <span className="bh-meta-num">{m ? m.legenda : '— / — itens'}</span>
              </div>
              <div
                className={`bh-barra${m?.batida ? ' bh-barra-ok' : ''}`}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div style={{ width: `${String(pct)}%` }} />
              </div>
              <div className="bh-falta">
                {m === null
                  ? ' '
                  : m.batida
                    ? 'Meta da semana batida (itens confirmados com o HOS).'
                    : `Faltam ${String(faltam)} ${faltam === 1 ? 'item confirmado' : 'itens confirmados'} até sexta. Vale o que bate com o cupom do HOS, menos devolução e cancelamento.`}
              </div>
              <div className="bh-caixas">
                {!ocultos && (
                  <a className="bh-caixa bh-caixa-ouro" href="#/premiacao" data-tour="premiacao">
                    <div className="bh-caixa-rotulo">PREMIAÇÃO LANÇADA</div>
                    <div className="bh-caixa-valor">
                      {r ? reais(r.premiacaoPrevistaSemana) : '—'}
                    </div>
                    <div className="bh-caixa-sub">a confirmada com o HOS: toque aqui</div>
                  </a>
                )}
                <div className="bh-caixa">
                  <div className="bh-caixa-rotulo">ITENS HOJE</div>
                  <div className="bh-caixa-valor">{r ? r.itensHoje : '—'}</div>
                </div>
              </div>
            </div>
          </section>

          <Vencendo />

          <a className="bh-pagando" href="#/pagando">
            <span className="bh-pagando-titulo">O que está pagando</span>
            <span className="bh-pagando-sub">
              Vitamina, pré-vencido e paradão: por quanto vender
              {ocultos ? '' : ' e quanto você ganha'}
            </span>
          </a>

          <section className="bh-cartao bh-ultimas" aria-label="Suas vendas de hoje">
            <div className="bh-cartao-topo">
              <h2>
                <span className="bh-so-movel">Últimas vendas</span>
                <span className="bh-so-pc">Suas vendas de hoje</span>
              </h2>
              {r && (
                <span className="bh-so-pc bh-cartao-nota">
                  {r.itensHoje} {r.itensHoje === 1 ? 'item' : 'itens'}
                  {mostraValor(ocultos, r.premiacaoHoje) && ` · ${reais(r.premiacaoHoje)}`}
                </span>
              )}
            </div>
            {r !== null && vendasHoje.length === 0 && (
              <div className="bh-vazio">Nenhuma venda registrada hoje ainda.</div>
            )}
            {vendasHoje.map((v) => (
              <LinhaDeVenda key={v.id} v={v} />
            ))}
          </section>

          <section className="bh-cartao bh-mais" aria-label="Seus mais vendidos">
            <h2>Seus mais vendidos</h2>
            {r !== null && r.maisVendidos.length === 0 && (
              <div className="bh-vazio bh-vazio-mais">
                Aparece aqui depois das primeiras vendas.
              </div>
            )}
            {r?.maisVendidos.map((m) => (
              <a
                key={m.productId}
                className="bh-mais-linha"
                href={`#/vendas?produto=${m.productId}`}
              >
                <span>{m.descricao}</span>
                <span className="bh-mono">{m.vezes}×</span>
              </a>
            ))}
          </section>
        </div>
        {eu && podeFazer(eu, 'controlados.conferir') && (
          <a className="bh-como" href="#/controlados">
            Controlados do dia: conferir o que saiu do armário ontem
          </a>
        )}
        <a className="bh-como" href="#/escala?aba=minha">
          Minha escala: folgas, horários e pedidos
        </a>
        <a className="bh-como" href="#/como-funciona">
          Como funciona a premiação?
        </a>
      </div>
    </>
  );
}

function LinhaDeVenda({ v }: { v: MinhaVenda }) {
  const ocultos = useValoresOcultos();
  return (
    <div className="bh-venda">
      <span className="bh-venda-hora bh-so-pc">{hora(v.registradoEm)}</span>
      <div className="bh-venda-texto">
        <div className="bh-venda-nome">{v.descricao}</div>
        <div className="bh-venda-sub bh-so-movel">
          {hora(v.registradoEm)} · {v.quantidade} un · {ROTULO_DO_PROGRAMA[v.programa]}
        </div>
      </div>
      <span className="bh-venda-sigla bh-so-pc">{SIGLA_DO_PROGRAMA[v.programa]}</span>
      <span className="bh-venda-qtd bh-so-pc">{v.quantidade} un</span>
      {mostraValor(ocultos, v.premioTotal) && (
        <span className="bh-venda-valor">{reais(v.premioTotal)}</span>
      )}
    </div>
  );
}

function textoDosProgramas(r: ResumoDoBalconista | null): string {
  if (!r) return 'Vitamina, pré-vencido ou paradão';
  const nomes = (['vitamina', 'pre_vencido', 'paradao'] as const)
    .filter((p) => r.programas[p])
    .map((p) => ROTULO_DO_PROGRAMA[p]);
  if (nomes.length === 0) return 'Nenhuma categoria liberada agora';
  const texto =
    nomes.length === 1
      ? (nomes[0] ?? '')
      : `${nomes.slice(0, -1).join(', ')} ou ${nomes.at(-1) ?? ''}`;
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function diaDaSemana(): string {
  const d = new Date();
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'America/Sao_Paulo' });
  const dia = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
  return `${semana.split('-')[0] ?? semana}, ${dia}`;
}

function diaPorExtenso(): string {
  const d = new Date();
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'America/Sao_Paulo' });
  const resto = d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Sao_Paulo',
  });
  const s = semana.split('-')[0] ?? semana;
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}, ${resto}`;
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * O pódio do mês (dono, 27/09/2026: os 3 que mais venderam, para todos — só nome e posição, sem
 * valor) e os aniversariantes de hoje. Sem nada, some.
 */
function DestaquesDoDia() {
  const [d, setD] = useState<Destaques | null>(null);
  useEffect(() => {
    void lerDestaques().then((r) => r.ok && setD(r.dados));
  }, []);
  const podio = d?.vendedoresDoMes.podio ?? [];
  if (!d || (podio.length === 0 && d.aniversariantes.length === 0)) return null;
  const mes = MESES[Number(d.vendedoresDoMes.mes.slice(5, 7)) - 1] ?? '';
  return (
    <div className="bh-destaques">
      {podio.length > 0 && (
        <div className="bh-destaque bh-destaque-ouro">
          <Icone nome="trophy" tamanho={16} traco={1.8} />
          <span>
            Vendedores de {mes}:{' '}
            {podio.map((x, i) => (
              <span key={`${String(x.posicao)}-${x.nome}`}>
                {i > 0 && ' · '}
                {x.posicao}º <b>{x.nome}</b>
              </span>
            ))}
          </span>
        </div>
      )}
      {d.aniversariantes.length > 0 && (
        <div className="bh-destaque">
          <Icone nome="cal" tamanho={16} traco={1.8} />
          <span>
            Hoje é aniversário de <b>{d.aniversariantes.join(', ')}</b>. Parabéns!
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Pré-vencidos que vencem nos próximos 7 dias ou já venceram neste mês (ainda valem até o fim do
 * mês — os mais urgentes, item 33): o que vender primeiro. Sem nada, some.
 */
function Vencendo() {
  const ocultos = useValoresOcultos();
  const [v, setV] = useState<VencendoNaSemana | null>(null);
  useEffect(() => {
    void vencendoNaSemana().then((r) => r.ok && setV(r.dados));
  }, []);
  if (!v || v.itens.length === 0) return null;
  return (
    <section className="bh-cartao bh-vencendo" aria-label="Vencendo esta semana">
      <div className="bh-cartao-topo">
        <h2>Vencendo esta semana</h2>
        <a href="#/pagando">Ver todos{v.itens.length > 5 ? ` (${String(v.itens.length)})` : ''}</a>
      </div>
      <div className="bh-vencendo-lista">
        {v.itens.slice(0, 5).map((i) => (
          <a
            key={i.productId}
            className="bh-vencendo-item"
            href={`#/vendas?produto=${i.productId}`}
          >
            <span className={`bh-vencendo-dias${i.diasParaVencer <= 2 ? ' bh-urgente' : ''}`}>
              {quandoVence(i.diasParaVencer)}
            </span>
            <span className="bh-vencendo-nome">{i.descricao}</span>
            <span className="bh-vencendo-sub">
              {i.quantidade ? `${i.quantidade.replace('.', ',')} un · ` : ''}
              {ocultos || !mostraValor(ocultos, i.premio) ? (
                <>
                  {i.venderPor ? `base ${reais(i.venderPor)}` : ''}
                  {i.pmc ? `${i.venderPor ? ' · ' : ''}PMC ${reais(i.pmc)}` : ''}
                </>
              ) : (
                <>
                  {i.venderPor ? `base ${reais(i.venderPor)} ganha ` : ''}
                  <b>{reais(i.premio)}</b>
                  {i.pmc && i.premioNoPmc && (
                    <>
                      {` · PMC ${reais(i.pmc)} ganha `}
                      <b>{reais(i.premioNoPmc)}</b>
                    </>
                  )}
                </>
              )}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}
